import { Deferred, Head, Link, router } from "@inertiajs/react"
import { AlertTriangle, ChevronLeft } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { BlocklistConfirmation } from "@/components/candidates/blocklist-confirmation"
import { CandidateActionBar } from "@/components/candidates/candidate-action-bar"
import { CandidateContactPanel } from "@/components/candidates/candidate-contact-panel"
import { CandidateNotesEditor } from "@/components/candidates/candidate-notes-editor"
import { CandidateProfileCard } from "@/components/candidates/candidate-profile-card"
import { CandidateRemovedBanner } from "@/components/candidates/candidate-removed-banner"
import { CandidateStatusBadge } from "@/components/candidates/candidate-status-badge"
import {
  CandidateStickyDecisionBar,
  shouldRenderStickyDecisionBar,
} from "@/components/candidates/candidate-sticky-decision-bar"
import { CandidateTagsEditor } from "@/components/candidates/candidate-tags-editor"
import {
  CrossBrandSignalsCard,
  CrossBrandSignalsError,
  CrossBrandSignalsSkeleton,
} from "@/components/candidates/cross-brand-signals-card"
import { RevertConfirmation } from "@/components/candidates/revert-confirmation"
import {
  StatusHistoryTimeline,
  useCandidateStatusHistoryProps,
} from "@/components/candidates/status-history-timeline"
import {
  TiktokProfileSection,
  TiktokProfileSectionSkeleton,
} from "@/components/candidates/tiktok-profile-section"
import { UnblocklistConfirmation } from "@/components/candidates/unblocklist-confirmation"
import { InvitesPanel } from "@/components/invites/invites-panel"
import TiktokLinkStateCue from "@/components/tiktok-link-state-cue"
import TiktokVerifiedBadge from "@/components/tiktok-verified-badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { useOnline } from "@/hooks/use-online"
import AppLayout from "@/layouts/app-layout"
import { finalFocusByTestId } from "@/lib/final-focus"
import { cn } from "@/lib/utils"
import { candidatesPath } from "@/routes"
import type { CandidateShowProps } from "@/types/candidates"

/**
 * pages/Brand/Candidates/Show — OKS-18 Candidate Detail page.
 *
 * Inertia page consuming the BE-locked `CandidateShowProps` shape.
 *
 * **`cross_brand_signals` is deferred** (Inertia 2.x `Deferred`
 * component) — `null` on first paint, panel renders
 * `CrossBrandSignalsSkeleton` until hydrate. Skeleton remains visible
 * even after the rest of the page hydrates (UX Spec).
 *
 * **`actions` is the OKS-19 transition-URL shape** (`mark_reviewed_url` ·
 * `approve_url` · `reject_url` · `revert_url` ·
 * `blocklist_url` · `unblocklist_url`; OKS-566 dropped `mark_joined_url`,
 * OKS-674 dropped `invite_url` — inviting is now the Invitations
 * section's Generate-link alone).
 * Each
 * field is `string | null`; null = transition is state-illegal
 * (e.g. `approve_url` when `status != "reviewed"`) OR role-locked
 * (e.g. `blocklist_url` for non-OKS-Admin). `CandidateActionBar`
 * applies the **single hide-when-null visibility rule** — null URL
 * → button DOM-absent. The OKS-18 disabled-with-tooltip placeholder
 * path was deleted in F7 (commit `7585144`); the orphan
 * `approval_workflow_pending_tooltip` i18n key was swept in F12
 * (commit `c38bb02`).
 *
 * **`back_url` is server-supplied** (BE preserves the candidate-list
 * filter URL when reachable). FE never reconstructs from referrer.
 *
 * **`useFlash` is mounted** at the layout level via `PersistentLayout`
 * — this page does NOT mount it again. Toasts (`Notes saved`,
 * `Tags updated`) flow through `flash[:success]` from the controller.
 *
 * **5 states** — page-level loading skeleton not rendered here (Inertia
 * shows previous-page chrome during transition; the per-section
 * skeletons inside notes/tags/cross-brand cover the deferred-prop
 * loading); error/offline handled at section level.
 *
 * **Locked QA testid contract** (39 testids transcribed verbatim from
 * UX Spec). P0 brand-isolation rule: NO `…-brand-name`/`…-brand-id`/
 * `…-brand-slug` slots anywhere — enforced by
 * `cross_brand_leak_matcher.rb` + Playwright negative assertion.
 */
export default function CandidateShow({
  candidate,
  status_history,
  cross_brand_signals,
  tiktok_profile_embed,
  actions,
  revert_destination,
  appeal_state,
  can_edit,
  can_edit_profile,
  back_url,
  brand_tags,
  invite,
  invite_actions,
  contact,
  removal,
  removal_actions,
  self_review_advisory,
}: CandidateShowProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const candidateTimelineProps = useCandidateStatusHistoryProps()

  // OKS-951 — the « À relancer » card's « Relancer » action deep-links here
  // with `?focus=contact`, so the panel scrolls into view on arrival. Read from
  // the URL rather than a prop: it is a per-visit navigation intent, not state
  // the server owns, and threading it through the props would make the
  // controller responsible for where the browser scrolls.
  const focusContact =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("focus") === "contact"
  // Phase 4 F5 — `useFlash` is mounted by `PersistentLayout` once for
  // the whole app; calling it on the page would double-toast every
  // success. Removed.

  // Cross-brand deferred-prop error tracking. Inertia 2.x's `<Deferred>`
  // doesn't ship a built-in error callback; we listen at the router-event
  // level for visit failures + flip a local boolean when the failure
  // occurred during a `cross_brand_signals` partial reload. The retry
  // button does its own `router.reload({ only: ['cross_brand_signals'] })`
  // and clears the error flag on the next visit start.
  //
  // V1 stub returns zeros (never throws), so this path is technically
  // unreachable until OKS-20 lands a real signals service. Wired now so
  // the locked testid contract (`candidate-detail-cross-brand-error` +
  // `…-retry`) is honored without a Phase-3 churn rename and so QA's
  // boundary test has a target.
  const [crossBrandError, setCrossBrandError] = useState(false)

  // OKS-19 F8 — RevertConfirmation modal state. Show.tsx owns the
  // modal; CandidateActionBar's onRevertClick callback opens it.
  // Modal is mounted only when `actions.revert_url` is non-null
  // (BE serializes non-null when current_status ∈ {approved, rejected}).
  const [revertOpen, setRevertOpen] = useState(false)

  // OKS-19 F9 — Blocklist + Unblocklist modals (OKS-Admin-only,
  // hidden-when-null via the F7 visibility rule). Mounted only when
  // their respective URLs are non-null.
  const [blocklistOpen, setBlocklistOpen] = useState(false)
  const [unblocklistOpen, setUnblocklistOpen] = useState(false)

  // OKS-998 — the reject modal's open state is lifted HERE so the sticky
  // decision bar's Reject can drive the modal that `CandidateActionBar`
  // still owns. The flow itself (picker, note, submit) stays in the action
  // bar; only the boolean moves. That keeps exactly ONE
  // `RejectionReasonPicker` and one `candidate-detail-reject-confirm` on
  // the page, which page-scoped Playwright selectors depend on.
  //
  // 🔴 This rests on `CandidateActionBar` being mounted UNCONDITIONALLY
  // below, and on it mounting its reject modal ungated on `reject_url`.
  // If either becomes conditional, the sticky Reject silently opens
  // nothing — a failure no server-side instrument can see.
  const [rejectOpen, setRejectOpen] = useState(false)
  // Which trigger opened it, so focus returns to THAT button on close.
  // `CandidateActionBar`'s own Reject is the sole caller of
  // `onRejectOpenChange(true)`, so the two setters cannot race.
  const [rejectOrigin, setRejectOrigin] = useState<"header" | "sticky">(
    "header",
  )
  const rejectFinalFocus = finalFocusByTestId(
    rejectOrigin === "sticky"
      ? "candidate-detail-sticky-reject"
      : "candidate-detail-reject",
  )
  const showStickyDecisionBar = shouldRenderStickyDecisionBar(actions)
  useEffect(() => {
    const offError = router.on("error", (event) => {
      // Inertia router error event carries an `errors` payload + the
      // visit's `params`. We can't introspect `only:` here; conservatively
      // flip the flag for any visit error and let the retry button reset.
      // FE-side this is best-effort — BE returning the deferred prop
      // OK trumps any stale error flag (the next render shows the panel).
      void event
      setCrossBrandError(true)
    })
    const offStart = router.on("start", () => setCrossBrandError(false))
    return () => {
      offError()
      offStart()
    }
  }, [])

  const backHref = back_url || candidatesPath()

  return (
    <AppLayout>
      <Head
        title={t("candidates.detail.head_title", {
          handle: candidate.tiktok_handle,
        })}
      />
      <div
        data-testid="candidate-detail-page"
        className={cn(
          "flex flex-col gap-6 p-6",
          // OKS-998 — clearance for the fixed sticky decision bar, applied
          // ONLY when that bar mounts. Unconditional padding would leave a
          // phantom gap at `new`/`joined`/`rejected`/`blocklisted`/removed,
          // which is exactly what BRIEF AC5 is falsified by. Both this and
          // the bar read the SAME predicate so they cannot drift.
          showStickyDecisionBar && "pb-24",
        )}
      >
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Link
              href={backHref}
              data-testid="candidate-detail-back"
              className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
              {t("candidates.detail.back")}
            </Link>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href={candidatesPath()}>
                      {t("candidates.detail.breadcrumb_root")}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <div className="flex items-center gap-2">
                      <h1
                        data-testid="candidate-detail-handle"
                        className="truncate font-mono text-xl font-semibold"
                      >
                        @{candidate.tiktok_handle}
                      </h1>
                      {/* OKS-23 — Verified badge when the candidate has an
                          active TikTok SocialAccount (OAuth-verified at
                          signup or relink). Renders nothing when
                          `tiktok_verified` is falsy. */}
                      <TiktokVerifiedBadge
                        verified={candidate.tiktok_verified ?? false}
                      />
                      {/* OKS-23 — Link-state cue for non-active states.
                          Renders nothing when `tiktok_link_state` is null
                          (active links keep the page quiet). */}
                      <TiktokLinkStateCue
                        state={candidate.tiktok_link_state ?? null}
                      />
                    </div>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <div className="flex items-center gap-2">
              <CandidateStatusBadge
                status={candidate.status}
                testId="candidate-detail-status-badge"
              />
            </div>
          </div>
          <CandidateActionBar
            actions={actions}
            // OKS-998 — controlled reject-modal state, shared with the
            // sticky decision bar at the bottom of this page.
            rejectOpen={rejectOpen}
            onRejectOpenChange={(open) => {
              if (open) setRejectOrigin("header")
              setRejectOpen(open)
            }}
            rejectFinalFocus={rejectFinalFocus}
            onRevertClick={() => setRevertOpen(true)}
            onBlocklistClick={() => setBlocklistOpen(true)}
            onUnblocklistClick={() => setUnblocklistOpen(true)}
            removeUrl={removal_actions.remove_url}
            handle={candidate.tiktok_handle}
            // OKS-952 — the from_status a rejection is recorded against.
            // This page is the only surface reaching all five rejectable
            // statuses, so gate separation is verifiable here.
            status={candidate.status}
            className="sm:flex-shrink-0"
          />
          {/*
            OKS-19 F8 — RevertConfirmation modal. Mounted only when
            BE has supplied a revert_url (i.e. status ∈ {approved,
            rejected} for any seated user). Modal owns its own form
            submit; F7's CandidateActionBar onRevertClick callback
            triggers `setRevertOpen(true)`.
          */}
          {/*
            OKS-948 — `revert_destination` is non-null exactly when
            `revert_url` is (server invariant), so this narrowing never
            drops a mountable modal. The `?? "reviewed"` is a type-level
            floor only; if it ever renders, the server broke the invariant.
          */}
          {actions.revert_url && (
            <RevertConfirmation
              open={revertOpen}
              onOpenChange={setRevertOpen}
              actionUrl={actions.revert_url}
              destination={revert_destination ?? "reviewed"}
              appealState={appeal_state}
              onSuccess={() => setRevertOpen(false)}
            />
          )}
          {/*
            OKS-950 (R4) — the used-appeal line.

            ⚠️ TEXT, deliberately, rendered in the reading position the
            Move-back button occupied. A spent appeal removes `revert_url`,
            and a button that simply VANISHES is indistinguishable from one
            that was never legal at this status — leaving the agency unable to
            tell "you have had your reply" from "this action doesn't apply
            here". The absence is explained in order rather than discovered by
            its absence (UX Spec §3 Edge, §6).

            NOT a disabled button with a tooltip: that path is explicitly
            refused, and a tooltip is unreachable by touch anyway.

            `appeal_state === "spent"` is the only signal that can drive this.
            It cannot be inferred from `revert_url === null`, which is equally
            null for every status where a revert was never legal.
          */}
          {/*
            OKS-958 item 3 — INERT live region (1 of 3 IN THIS FILE'S SCOPE,
            i.e. this file + `candidate-removed-banner.tsx`; NOT 1 of 3 on the
            rendered page — see the sweep warning below). Records a decision;
            changes nothing.

            role="status" implies aria-live="polite", but this region is
            record-derived: `appeal_state` arrives as an Inertia prop and an
            Inertia re-render RECONCILES rather than remounts, so no mutation
            ever reaches the region and nothing is ever announced. The role is
            therefore inert rather than wrong.

            🔴 It is NOT being changed, and the reason is not inertia (ours or
            the DOM's): no instrument in this stack can tell us a change was
            correct. Playwright, Capybara and jsdom all read the ATTRIBUTE;
            none observes a screen reader. Changing a live-region role with no
            way to detect getting it wrong trades a harmless no-op for an
            undetectable regression. Blocked on one NVDA/VoiceOver pass, which
            nobody has run (OKS-954 manual check §B asked for exactly this).

            ⚠️ If you are sweeping role="status": OKS-958 enumerated FOUR
            regions, and that number is scoped to THIS FILE plus
            `candidate-removed-banner.tsx` — it is NOT the number on the
            rendered page. Measured with every branch on, the page carries
            EIGHT, including the shell's role badge, the status badge, the
            cross-brand loading region, and — critically — a SECOND genuinely
            live `useOnline()` notice inside
            `components/candidates/candidate-notes-editor.tsx`, which no
            OKS-958 document enumerates.

            The four in scope here: this one and the self-review advisory
            below are inert; `candidate-removed-banner.tsx` is inert; the
            offline notice below is LIVE and CORRECT.

            🔴 Do not trust the number — re-enumerate before you sweep. An
            undercount is worse than no record for this failure mode: it
            stops the reader early, and what it leaves behind is a live
            region. See OKS-958 BRIEF D5.
          */}
          {appeal_state === "spent" && (
            <p
              data-testid="candidate-detail-appeal-spent"
              role="status"
              className="text-muted-foreground text-sm"
            >
              {t("candidates.detail.actions.appeal_spent")}
            </p>
          )}
          {/*
            OKS-19 F9 — BlocklistConfirmation (REQUIRED reason) +
            UnblocklistConfirmation (no-reason). OKS-Admin-only —
            BE serializes the URLs non-null only for admin actors,
            and the F7 visibility rule renders the trigger buttons
            iff URL non-null. Modals mounted iff URL non-null
            (defensive — prevents passing undefined actionUrl).
          */}
          {actions.blocklist_url && (
            <BlocklistConfirmation
              open={blocklistOpen}
              onOpenChange={setBlocklistOpen}
              actionUrl={actions.blocklist_url}
              onSuccess={() => setBlocklistOpen(false)}
            />
          )}
          {actions.unblocklist_url && (
            <UnblocklistConfirmation
              open={unblocklistOpen}
              onOpenChange={setUnblocklistOpen}
              actionUrl={actions.unblocklist_url}
              onSuccess={() => setUnblocklistOpen(false)}
            />
          )}
        </header>

        {/*
          OKS-958 item 3 — 🔴 THIS ONE IS CORRECT. DO NOT SWEEP IT.

          Unlike the three INERT role="status" regions in this file's scope
          (the appeal-spent note above, the self-review advisory below, and
          `candidate-removed-banner.tsx`) this region is GENUINELY LIVE:
          `useOnline()` flips `online` from a browser event, mid-session,
          while focus is elsewhere. That is precisely the case aria-live
          exists for, and announcing it politely is the desired behaviour.

          The other three are record-derived and never mutate, so they are
          inert. This one is not. A blanket "these live regions never fire,
          drop the role" sweep across the page would leave the three no-ops
          untouched in effect and silently break the ONE region doing real
          work — which is the trap OKS-958 was filed to prevent.

          If you change this, you are changing behaviour a user can observe.
          The inert three are a documentation question; this is not.
        */}
        {!online && (
          <p
            role="status"
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          >
            {t("candidates.detail.states.offline_banner")}
          </p>
        )}

        {/*
          OKS-949 — self-review advisory (UX Spec §2). Page-level banner strip,
          immediately AFTER the offline notice.

          🔴 Not "above the action bar", despite the ticket. The action bar is a
          right-hand child of the <header> above, and OKS-673 moved the primary
          green actions (Mark as reviewed / Approve) out of it entirely into the
          Invitations panel in the right column — so a banner glued to the action
          bar would sit nowhere near the button the sourcer will actually press.
          Reading order, measured against this file rather than assumed:
          CandidateActionBar (in <header>) → this banner → InvitesPanel. So the
          banner PRECEDES the primary green action at `new` and `reviewed`, the
          two most common gate statuses, and FOLLOWS the header bar everywhere.
          At `pending_brand_validation` the offered set is validate + reject,
          both header-bar affordances, so there the banner follows all of them.
          Accepted: the only placement that would precede the header bar is
          above the candidate's own identity, which is worse.

          UX Spec §2 is binding here and supersedes the ticket.

          🔴 ADVISORY ONLY — nothing below this line disables, hides, greys,
          confirms or tooltips anything. Every button that renders today for the
          sourcer still renders, still enables, still works (epic OKS-947 D3).
          Crew's hide-the-affordance-when-denied convention does NOT apply:
          nothing is denied.

          `=== true`, not truthy: UX Spec §5 Error says an absent or malformed
          signal must render NOTHING — a false accusation is a worse failure
          than a missed nudge.

          Inline rather than a component, matching the offline notice above: a
          wrapper would take no props (the gating lives here), so its test could
          only assert that a static string renders — true on every build,
          including a broken one. `CandidateRemovedBanner` is a component
          because it owns state, a dialog and a POST; this owns nothing.

          Amber, not destructive red — red is the removed banner's, and this is
          a nudge, not a problem. Colour is decoration only; the sentence
          carries the whole meaning and the glyph is aria-hidden.

          OKS-958 item 3 — INERT live region (2 of 3 in this file's scope, not
          on the rendered page). Records a decision;
          changes nothing. `self_review_advisory` is an Inertia prop and the
          re-render reconciles rather than remounts, so the region never
          mutates and nothing is announced. Not being changed because no
          instrument here can detect a wrong change (Playwright/Capybara/jsdom
          read the attribute; none observes a screen reader); blocked on an
          NVDA/VoiceOver pass nobody has run.

          ⚠️ `spec/system/candidates/self_review_advisory_spec.rb` asserts
          `banner[:role] == "status"` on this element. That spec moves only if
          this role moves — and it does not move in OKS-958. If you change the
          role, that assertion is part of your diff, in the same commit.

          ⚠️ The FOUR regions OKS-958 enumerated are scoped to this file plus
          `candidate-removed-banner.tsx` — the rendered page carries EIGHT.
          Of the four: this one and the appeal-spent note above are inert,
          `candidate-removed-banner.tsx` is inert, the offline notice above is
          live and CORRECT. Beyond them, `candidate-notes-editor.tsx` holds a
          SECOND live `useOnline()` notice. Re-enumerate before sweeping; do
          not sweep them together.
        */}
        {self_review_advisory === true && (
          <div
            data-testid="candidate-detail-self-review-banner"
            role="status"
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          >
            <AlertTriangle
              className="mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            <span>{t("candidates.detail.self_review_banner.message")}</span>
          </div>
        )}

        {/*
          OKS-465 — removed-state banner. Shows when the candidate is
          soft-removed; carries the Restore affordance (gated on
          `removal_actions.restore_url`). The pipeline action bar above
          renders empty while removed (BE nulls every transition URL).
        */}
        {removal && (
          <CandidateRemovedBanner
            removal={removal}
            restoreUrl={removal_actions.restore_url}
            handle={candidate.tiktok_handle}
          />
        )}

        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="flex flex-col gap-6">
            <CandidateProfileCard
              candidate={candidate}
              canEdit={can_edit}
              canEditProfile={can_edit_profile}
            />
            {/*
              OKS-628 — TikTok profile embed. Deferred prop (mirrors
              cross_brand_signals) so the detail paints without waiting on
              the external oEmbed fetch. By the time Deferred passes
              children, `tiktok_profile_embed !== null`.

              OKS-1007 — the skeleton and BOTH resolved arms (embed,
              fallback) reserve the same `min-h-[220px]` floor, so this swap
              does not move the contact panel below it. The old wording said
              the skeleton held the footprint "then either the embed or the
              fallback renders" — the either/or was the bug: only the embed
              arm actually reserved, and the fallback arm contracted the
              section 44px, retargeting clicks on the panel's 16px radios.

              OKS-1015 — the floor covers only the deferred swap. `embed.js`
              injecting its <iframe> later still grows the embed past the
              floor (uncapped by decision: a cap crops a real embed), so a
              press on the panel is protected by `TiktokProfileEmbed`
              freezing its own height for the press. A shift that ends
              BEFORE the press is ordinary layout shift, not covered.
            */}
            <Deferred
              data="tiktok_profile_embed"
              fallback={<TiktokProfileSectionSkeleton />}
            >
              {tiktok_profile_embed !== null ? (
                <TiktokProfileSection
                  embed={tiktok_profile_embed}
                  handle={candidate.tiktok_handle}
                />
              ) : (
                <TiktokProfileSectionSkeleton />
              )}
            </Deferred>
            <CandidateNotesEditor candidate={candidate} canEdit={can_edit} />
            {/*
              OKS-951 — contact panel, BETWEEN the notes editor and the timeline
              (UX Spec §1). Renders only when the server sends `contact`; null
              means DOM-absent, never disabled — the same hide-when-null rule the
              action bar uses, so there is no status logic on this page.

              `?focus=contact` arrives from the « À relancer » card's « Relancer »
              deep link and scrolls the panel into view.
            */}
            {contact && (
              <CandidateContactPanel
                contact={contact}
                handle={candidate.tiktok_handle}
                autoFocus={focusContact}
              />
            )}
            <StatusHistoryTimeline
              events={status_history}
              {...candidateTimelineProps}
            />
          </div>
          <div className="flex flex-col gap-6">
            {/*
              OKS-21 / OKS-673 — Invites panel. Renders inside the
              candidate detail right column (UX Spec §2.1). Empty-state
              variants for `approved`/`not_approved` are decided by
              backend's `invite_actions.generate_url` discriminator
              (non-null = approved AND policy allows). OKS-673 threads
              the candidate `actions` surface in so the pre-approval
              empty state renders the inline green-flow advance action
              (Mark as reviewed / Approve) off the same `*_url`
              nullability. FE never re-derives from `candidate.status`.
            */}
            <InvitesPanel
              invite={invite}
              invite_actions={invite_actions}
              actions={actions}
              candidate_handle={candidate.tiktok_handle}
            />
            {crossBrandError ? (
              <CrossBrandSignalsError
                onRetry={() => {
                  setCrossBrandError(false)
                  router.reload({ only: ["cross_brand_signals"] })
                }}
              />
            ) : (
              <Deferred
                data="cross_brand_signals"
                fallback={<CrossBrandSignalsSkeleton />}
              >
                {/*
                  Render-prop pattern: when the deferred prop hydrates,
                  Inertia replaces the fallback with this child tree.
                  We re-read `cross_brand_signals` from props via the
                  same `usePage` snapshot the page closure is built off
                  of — but Deferred passes children only after hydration,
                  so by the time this renders, `cross_brand_signals !==
                  null`. The non-null assertion is safe.
                */}
                {cross_brand_signals !== null ? (
                  <CrossBrandSignalsCard signals={cross_brand_signals} />
                ) : (
                  <CrossBrandSignalsSkeleton />
                )}
              </Deferred>
            )}
            <CandidateTagsEditor
              candidateId={candidate.id}
              tags={candidate.tags}
              canEdit={can_edit}
              brandTags={brand_tags}
            />
          </div>
        </div>
        {/*
          OKS-998 — sticky decision bar. LAST child of the container on
          purpose: it is visually pinned but must come last in reading and
          tab order, after the profile it is a decision about (BRIEF §5
          a11y). It self-hides when neither action is available; the
          container's `pb-24` above is gated on the same predicate.
        */}
        {showStickyDecisionBar && (
          <CandidateStickyDecisionBar
            actions={actions}
            onRejectClick={() => {
              setRejectOrigin("sticky")
              setRejectOpen(true)
            }}
          />
        )}
      </div>
    </AppLayout>
  )
}

// Re-export error retry button so a future error-boundary parent can
// expose it without re-importing — symmetry with OKS-17.
export function CandidateDetailErrorRetry({
  onRetry,
}: {
  onRetry?: () => void
}) {
  const { t } = useTranslation()
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => (onRetry ? onRetry() : router.reload())}
      data-testid="candidate-detail-error-retry"
    >
      {t("candidates.detail.states.error_retry")}
    </Button>
  )
}
